import React, { useState, useEffect } from 'react';
import { Check, Download, Building2, ShoppingCart, Briefcase, LogOut, Settings, Save, Share2, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBrand } from '../contexts/BrandContext';
import { useToast } from '../contexts/ToastContext';
import { usePricing, useDiscountCodes } from '../hooks/usePricing';
import { supabase } from '../lib/supabase';
import { createOpportunityFromEstimate } from '../lib/crmService';
import { generateAgreementWithClaude } from '../lib/agreementService';
import AdminPanel from './AdminPanel';
import SummaryPanel from './SummaryPanel';
import CustomSelect from './CustomSelect';
import jsPDF from 'jspdf';
import {
  PDF_TYPOGRAPHY,
  hexToRgb,
  checkPageBreak,
  setColor,
  renderPageTitle,
  renderSectionHeader,
  renderServiceItem,
  renderAdditionalItem,
  renderFeaturesToPDF,
  getBrandTheme,
} from '../lib/pdfConfig';

const WHITE_LOGO_BASE64 = 'data:image/png;base64,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';

export default function DynamicCalculator() {
  const { profile, signOut } = useAuth();
  const { brands, selectedBrand, setSelectedBrand } = useBrand();
  const toast = useToast();
  const { servicePricing, additionalPricing, supportPlans, getServicesByCategory, getAdditionalPrice, loading: pricingLoading } = usePricing();
  const { validateCode, mapCategoryToBreakdownKey } = useDiscountCodes();

  const [clientName, setClientName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientType, setClientType] = useState('local');
  const [includeWeb, setIncludeWeb] = useState(false);
  const [includeMarketing, setIncludeMarketing] = useState(true);
  const [pkg, setPkg] = useState('');
  const [seoTier, setSeoTier] = useState<string | null>(null);
  const [emailTier, setEmailTier] = useState<string | null>(null);
  const [selectedAdPlatforms, setSelectedAdPlatforms] = useState<string[]>([]);
  const [croTier, setCroTier] = useState<string | null>(null);
  const [multiLocation, setMultiLocation] = useState(0);
  const [addOns, setAddOns] = useState<string[]>([]);
  const [customWebHours, setCustomWebHours] = useState(0);
  const [customWebDesc, setCustomWebDesc] = useState('');
  const [customMarketingHours, setCustomMarketingHours] = useState(0);
  const [customMarketingDesc, setCustomMarketingDesc] = useState('');
  const [customPages, setCustomPages] = useState(0);
  const [sameLayoutPages, setSameLayoutPages] = useState(0);
  const [extraRevisions, setExtraRevisions] = useState(0);
  const [adSpend, setAdSpend] = useState(5000);
  const [discount, setDiscount] = useState<any>(null);
  const [discountInput, setDiscountInput] = useState('');
  const [webDiscountCode, setWebDiscountCode] = useState('');
  const [appliedWebDiscount, setAppliedWebDiscount] = useState<any>(null);
  const [webDiscountError, setWebDiscountError] = useState('');
  const [showAdmin, setShowAdmin] = useState(false);
  const [selectedSupportPlan, setSelectedSupportPlan] = useState<string | null>(null);
  const [expandedPackage, setExpandedPackage] = useState<string | null>(null);
  const [expandedService, setExpandedService] = useState<string | null>(null);
  const [industryCollapsed, setIndustryCollapsed] = useState(false);

  const fmt = (n: number) => '$' + n.toLocaleString();

  const renderStructuredDescription = (description: string | null) => {
    if (!description) return null;

    const parts = description.split(/(\*\*[^*]+\*\*)/g);

    return (
      <div className="space-y-3">
        {parts.map((part, idx) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return (
              <h5 key={idx} className="font-semibold text-sm text-gray-900 mt-3 first:mt-0">
                {part.slice(2, -2)}
              </h5>
            );
          } else if (part.trim()) {
            return (
              <p key={idx} className="text-sm text-gray-600 leading-relaxed">
                {part.trim()}
              </p>
            );
          }
          return null;
        })}
      </div>
    );
  };

  const renderStructuredFeatures = (features: any) => {
    if (!features) return null;

    // Handle structured features with sections property
    if (features.sections && Array.isArray(features.sections)) {
      return (
        <div className="space-y-6">
          {features.sections.map((section: any, sectionIdx: number) => (
            <div key={sectionIdx}>
              {section.title && (
                <h5 className="font-semibold text-sm text-gray-900 mb-2">{section.title}</h5>
              )}
              {section.subtitle && (
                <p className="text-sm text-gray-600 mb-3 leading-relaxed">{section.subtitle}</p>
              )}
              {section.description && (
                <p className="text-sm text-gray-600 mb-3 leading-relaxed">{section.description}</p>
              )}
              {section.items && section.items.length > 0 && (
                <ul className="space-y-1.5">
                  {section.items.map((item: string, itemIdx: number) => (
                    <li key={itemIdx} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      );
    }

    // Handle array of section objects (stored as direct array)
    if (Array.isArray(features) && features.length > 0 && typeof features[0] === 'object' && features[0].title) {
      return (
        <div className="space-y-6">
          {features.map((section: any, sectionIdx: number) => (
            <div key={sectionIdx}>
              {section.title && (
                <h5 className="font-semibold text-sm text-gray-900 mb-2">{section.title}</h5>
              )}
              {section.subtitle && (
                <p className="text-sm text-gray-600 mb-3 leading-relaxed">{section.subtitle}</p>
              )}
              {section.description && (
                <p className="text-sm text-gray-600 mb-3 leading-relaxed">{section.description}</p>
              )}
              {section.items && section.items.length > 0 && (
                <ul className="space-y-1.5">
                  {section.items.map((item: string, itemIdx: number) => (
                    <li key={itemIdx} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      );
    }

    // Handle simple array of strings (legacy format)
    if (Array.isArray(features)) {
      return (
        <ul className="space-y-1.5">
          {features.map((feature: string, idx: number) => (
            <li key={idx} className="flex items-start gap-2 text-sm">
              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      );
    }

    return null;
  };

  const calcWeb = () => {
    if (!includeWeb) return { total: 0, originalTotal: 0, discount: 0 };

    const breakdown: Record<string, number> = {
      website_packages: 0,
      web_addons: 0,
    };

    const pkgService = getServicesByCategory('website_packages').find(s => s.tier_key === pkg);
    breakdown.website_packages = pkgService?.price || 0;

    addOns.forEach(id => {
      const addon = getServicesByCategory('web_addons').find(s => s.tier_key === id);
      if (addon) breakdown.web_addons += addon.price;
    });

    breakdown.web_addons += customPages * getAdditionalPrice('custom_page');
    breakdown.web_addons += sameLayoutPages * getAdditionalPrice('same_layout_page');
    breakdown.web_addons += extraRevisions * getAdditionalPrice('extra_revision');
    breakdown.web_addons += customWebHours * getAdditionalPrice('hourly_rate');

    let total = Object.values(breakdown).reduce((sum, val) => sum + val, 0);
    const originalTotal = total;
    let discAmount = 0;

    if (appliedWebDiscount) {
      if (appliedWebDiscount.applies_to_services && appliedWebDiscount.applies_to_services.length > 0) {
        const processedKeys = new Set<string>();

        appliedWebDiscount.applies_to_services.forEach((serviceCategory: string) => {
          const breakdownKey = mapCategoryToBreakdownKey(serviceCategory);

          if (breakdown[breakdownKey] > 0 && !processedKeys.has(breakdownKey)) {
            processedKeys.add(breakdownKey);
            const serviceTotal = breakdown[breakdownKey];

            if (!appliedWebDiscount.min_spend || serviceTotal >= appliedWebDiscount.min_spend) {
              let serviceDiscount = 0;
              if (appliedWebDiscount.type === 'percentage' && appliedWebDiscount.value) {
                serviceDiscount = serviceTotal * (appliedWebDiscount.value / 100);
              } else if (appliedWebDiscount.type === 'fixed' && appliedWebDiscount.value) {
                serviceDiscount = appliedWebDiscount.value;
              }
              if (appliedWebDiscount.max_discount && serviceDiscount > appliedWebDiscount.max_discount) {
                serviceDiscount = appliedWebDiscount.max_discount;
              }
              discAmount += Math.min(serviceDiscount, serviceTotal);
            }
          }
        });
      } else {
        discAmount = appliedWebDiscount.type === 'percentage'
          ? total * (appliedWebDiscount.value / 100)
          : appliedWebDiscount.value;
      }
      total -= discAmount;
    }

    return { total, originalTotal, discount: discAmount };
  };

  const calcMarketing = () => {
    const breakdown: Record<string, number> = {
      support_plans: 0,
      seo: 0,
      email: 0,
      ppc: 0,
      cro: 0,
      other: 0,
    };

    // Support plans can be selected independently of marketing services
    if (selectedSupportPlan) {
      const plan = supportPlans.find(p => p.tier_key === selectedSupportPlan);
      if (plan) breakdown.support_plans += plan.price;
    }

    if (!includeMarketing) {
      // If only support plan is selected, return just that
      const monthly = breakdown.support_plans;
      return { monthly, setup: 0, first: monthly, disc: 0, breakdown };
    }

    if (seoTier) {
      const seo = getServicesByCategory('seo_services', clientType).find(s => s.tier_key === seoTier);
      if (seo) breakdown.seo += seo.price;
    }

    if (emailTier) {
      const email = getServicesByCategory('email_services', clientType).find(s => s.tier_key === emailTier);
      if (email) breakdown.email += email.price;
    }

    if (selectedAdPlatforms.length > 0) {
      const adServices = getServicesByCategory('ad_platforms', clientType);
      selectedAdPlatforms.forEach((platform, index) => {
        const ad = adServices.find(s => s.tier_key === platform);
        if (ad) {
          breakdown.ppc += index === 0 ? ad.price : (ad.additional_price || ad.price);
        }
      });
      breakdown.ppc += adSpend * 0.10;
    }

    if (clientType === 'ecommerce' && croTier) {
      const cro = getServicesByCategory('cro_services').find(s => s.tier_key === croTier);
      if (cro) breakdown.cro += cro.price;
    }

    if (multiLocation > 0) {
      breakdown.other += multiLocation * getAdditionalPrice('multi_location');
    }

    breakdown.other += customMarketingHours * getAdditionalPrice('hourly_rate');

    let monthly = Object.values(breakdown).reduce((sum, val) => sum + val, 0);
    let disc = 0;

    if (discount) {
      if (discount.type === 'conditional') {
        if (selectedAdPlatforms.length === 2) disc = discount.base_value;
        else if (selectedAdPlatforms.length >= 3) disc = discount.bonus_value;
      } else if (discount.applies_to_services && discount.applies_to_services.length > 0) {
        const processedKeys = new Set<string>();

        discount.applies_to_services.forEach((serviceCategory: string) => {
          const breakdownKey = mapCategoryToBreakdownKey(serviceCategory);

          if (breakdown[breakdownKey] > 0 && !processedKeys.has(breakdownKey)) {
            processedKeys.add(breakdownKey);
            const serviceTotal = breakdown[breakdownKey];

            if (!discount.min_spend || serviceTotal >= discount.min_spend) {
              let serviceDiscount = 0;
              if (discount.type === 'percentage' && discount.value) {
                serviceDiscount = serviceTotal * (discount.value / 100);
              } else if (discount.type === 'fixed' && discount.value) {
                serviceDiscount = discount.value;
              }
              if (discount.max_discount && serviceDiscount > discount.max_discount) {
                serviceDiscount = discount.max_discount;
              }
              disc += Math.min(serviceDiscount, serviceTotal);
            }
          }
        });
      } else {
        if (!discount.min_spend || monthly >= discount.min_spend) {
          if (discount.type === 'percentage' && discount.value) {
            disc = monthly * (discount.value / 100);
          } else if (discount.type === 'fixed' && discount.value) {
            disc = discount.value;
          }
          if (discount.max_discount && disc > discount.max_discount) {
            disc = discount.max_discount;
          }
        }
      }
    }

    const hasAds = selectedAdPlatforms.length > 0;
    const setupFee = getAdditionalPrice('setup_fee');
    const setup = (discount && discount.waive_setup && hasAds) ? 0 : (hasAds ? setupFee : 0);

    return { monthly: monthly - disc, setup, first: (monthly - disc) + setup, disc, breakdown };
  };

  async function handleApplyWebDiscount() {
    if (appliedWebDiscount) {
      setAppliedWebDiscount(null);
      setWebDiscountCode('');
      setWebDiscountError('');
    } else {
      const code = webDiscountCode.trim().toUpperCase();
      const discountCode = await validateCode(code, 'website');

      if (discountCode) {
        setAppliedWebDiscount(discountCode);
        setWebDiscountError('');
      } else {
        setWebDiscountError('Invalid discount code');
      }
    }
  }

  async function handleApplyMarketingDiscount() {
    if (discount) {
      setDiscount(null);
      setDiscountInput('');
    } else {
      const code = discountInput.trim().toUpperCase();
      const discountCode = await validateCode(code, 'marketing');

      if (discountCode) {
        setDiscount(discountCode);
      } else {
        alert('Invalid discount code');
      }
    }
  }

  async function handleSaveEstimate() {
    if (!clientName.trim()) {
      alert('Please enter a client name');
      return;
    }

    if (!selectedBrand || !profile) return;

    const webInfo = calcWeb();
    const marketingFees = calcMarketing();

    try {
      const { error } = await supabase.from('estimates').insert({
        brand_id: selectedBrand.id,
        user_id: profile.id,
        client_name: clientName,
        client_type: clientType,
        status: 'draft',
        config: {
          includeWeb,
          includeMarketing,
          pkg,
          seoTier,
          emailTier,
          selectedAdPlatforms,
          croTier,
          multiLocation,
          addOns,
          customWebHours,
          customWebDesc,
          customMarketingHours,
          customMarketingDesc,
          customPages,
          sameLayoutPages,
          extraRevisions,
          adSpend,
          webDiscount: appliedWebDiscount?.code,
          marketingDiscount: discount?.code,
          selectedSupportPlan,
        },
        totals: {
          web_total: webInfo.total,
          marketing_monthly: marketingFees.monthly,
          marketing_setup: marketingFees.setup,
          marketing_first: marketingFees.first,
          total_due: webInfo.total + marketingFees.first,
        },
      });

      if (error) throw error;
      alert('Estimate saved successfully!');
    } catch (error: any) {
      alert('Error saving estimate: ' + error.message);
    }
  }

  async function generateShareLink() {
    if (!selectedBrand || !profile) return;

    if (!clientName.trim()) {
      alert('Please enter a client name before sharing');
      return;
    }

    if (!clientEmail.trim()) {
      alert('Please enter a client email before sharing');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientEmail)) {
      alert('Please enter a valid email address');
      return;
    }

    const webInfo = calcWeb();
    const marketingFees = calcMarketing();

    try {
      const { data, error } = await supabase.from('estimates').insert({
        brand_id: selectedBrand.id,
        user_id: profile.id,
        client_name: clientName,
        company_name: companyName || clientName,
        email: clientEmail,
        client_type: clientType,
        status: 'sent',
        config: {
          includeWeb,
          includeMarketing,
          pkg,
          seoTier,
          emailTier,
          selectedAdPlatforms,
          croTier,
          multiLocation,
          addOns,
          customWebHours,
          customWebDesc,
          customMarketingHours,
          customMarketingDesc,
          customPages,
          sameLayoutPages,
          extraRevisions,
          adSpend,
          webDiscount: appliedWebDiscount?.code,
          marketingDiscount: discount?.code,
          selectedSupportPlan,
        },
        totals: {
          web_total: webInfo.total,
          web_original: webInfo.original,
          web_discount: webInfo.discount,
          marketing_monthly: marketingFees.monthly,
          marketing_setup: marketingFees.setup,
          marketing_first: marketingFees.first,
          marketing_discount: marketingFees.discount,
          total_due: webInfo.total + marketingFees.first,
        },
      }).select('id, shareable_id').single();

      if (error) throw error;

      if (!data?.shareable_id) {
        throw new Error('Failed to generate shareable link');
      }

      const baseUrl = window.location.origin;
      const shareUrl = `${baseUrl}?estimate=${data.shareable_id}`;

      console.log('Pre-generating agreement for estimate:', data.id);
      const agreementResult = await generateAgreementWithClaude({
        estimateId: data.id,
        brandId: selectedBrand.id,
        clientName,
        clientType,
        services: {
          includeWeb,
          includeMarketing,
          pkg,
          seoTier,
          emailTier,
          selectedAdPlatforms,
          croTier,
          multiLocation,
          addOns,
          customWebHours,
          customWebDesc,
          customMarketingHours,
          customMarketingDesc,
          customPages,
          sameLayoutPages,
          extraRevisions,
          adSpend,
          selectedSupportPlan,
        },
        totals: {
          webTotal: webInfo.total,
          marketingMonthly: marketingFees.monthly,
          marketingSetup: marketingFees.setup,
          marketingFirst: marketingFees.first,
          grandTotal: webInfo.total + marketingFees.first,
        },
        discounts: discount ? { code: discount.code } : undefined,
      });

      if (!agreementResult.success) {
        console.warn('Failed to pre-generate agreement:', agreementResult.error);
      } else {
        console.log('Agreement pre-generated successfully');
      }

      const opportunityResult = await createOpportunityFromEstimate(
        data.id,
        selectedBrand.id,
        shareUrl,
        clientEmail,
        companyName || clientName,
        webInfo.total + marketingFees.first
      );

      if (!opportunityResult.success) {
        console.error('Failed to create opportunity:', opportunityResult.error);
        toast.warning(
          `Estimate saved but failed to create opportunity: ${opportunityResult.error}\n\nThe share link was still created successfully.`,
          'Opportunity Creation Failed'
        );
      }

      navigator.clipboard.writeText(shareUrl).then(() => {
        if (opportunityResult.success) {
          toast.success(
            'The estimate has been saved, an opportunity has been created in your CRM, and the link is ready to share with your client.',
            'Share Link Copied!'
          );
        } else {
          toast.success(
            'The estimate has been saved and the link is ready to share with your client.\n\nNote: Opportunity creation failed - please create manually.',
            'Share Link Copied!'
          );
        }
      }).catch(() => {
        toast.info(`Copy this link to share with your client:\n\n${shareUrl}`, 'Share Link');
      });
    } catch (error: any) {
      toast.error('Error generating share link: ' + error.message, 'Share Link Error');
    }
  }

  const getImageDataURL = async (url: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          try {
            const dataURL = canvas.toDataURL('image/png');
            resolve(dataURL);
          } catch (e) {
            console.error('Canvas toDataURL error:', e);
            reject(e);
          }
        } else {
          reject(new Error('Could not get canvas context'));
        }
      };
      img.onerror = (e) => {
        console.error('Image load error:', e);
        reject(new Error('Failed to load image'));
      };
      img.src = url;
    });
  };

  async function exportPDF() {
    if (!selectedBrand) return;

    const doc = new jsPDF();
    const webInfo = calcWeb();
    const marketingFees = calcMarketing();

    const primaryRgb = hexToRgb(selectedBrand.primary_color);
    const secondaryRgb = hexToRgb(selectedBrand.secondary_color);
    const accentColor = PDF_TYPOGRAPHY.colors.accentColor;
    const darkTeal = PDF_TYPOGRAPHY.colors.darkTeal;
    const lightGray = PDF_TYPOGRAPHY.colors.lightGray;

    const brandTheme = getBrandTheme(selectedBrand.name, selectedBrand.primary_color, selectedBrand.secondary_color);
    const isWebDesignMiami = selectedBrand.name.toLowerCase().includes('web design miami');
    const isWestPalmBeach = selectedBrand.name.toLowerCase().includes('west palm beach');

    if (isWebDesignMiami || isWestPalmBeach) {
      doc.setFillColor(255, 255, 255);
      doc.rect(0, 0, 210, 45, 'F');
    } else {
      doc.setFillColor(primaryRgb.r, primaryRgb.g, primaryRgb.b);
      doc.rect(0, 0, 210, 45, 'F');
    }

    try {
      console.log('Attempting to load logo...');
      let logoUrl = '';

      if (isWebDesignMiami) {
        logoUrl = window.location.origin + '/Web Design Miami Company Logo copy.png';
      } else if (selectedBrand.logo_url) {
        logoUrl = selectedBrand.logo_url;
      } else {
        logoUrl = 'https://corsproxy.io/?https://solvedpuzzle.com/wp-content/uploads/2022/08/Recurso-1.png';
      }

      const logoDataURL = await getImageDataURL(logoUrl);
      console.log('Logo loaded successfully, adding to PDF');
      doc.addImage(logoDataURL, 'PNG', 15, 15, 70, 12);
    } catch (e) {
      console.error('Failed to load logo, using text fallback:', e);
      if (isWebDesignMiami || isWestPalmBeach) {
        doc.setTextColor(secondaryRgb.r, secondaryRgb.g, secondaryRgb.b);
      } else {
        doc.setTextColor(255, 255, 255);
      }
      doc.setFontSize(28);
      doc.setFont('helvetica', 'bold');
      doc.text(selectedBrand.name, 20, 28);
    }


    let y = 58;

    doc.setFillColor(lightGray.r, lightGray.g, lightGray.b);
    doc.roundedRect(PDF_TYPOGRAPHY.margins.page, y, PDF_TYPOGRAPHY.maxWidths.clientInfo, 36, 2, 2, 'F');

    doc.setDrawColor(primaryRgb.r, primaryRgb.g, primaryRgb.b);
    doc.setLineWidth(0.5);
    doc.roundedRect(PDF_TYPOGRAPHY.margins.page, y, PDF_TYPOGRAPHY.maxWidths.clientInfo, 36, 2, 2, 'D');

    setColor(doc, darkTeal);
    doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.clientInfoLabel);
    doc.setFont('helvetica', 'bold');
    doc.text('CLIENT INFORMATION', 20, y + 7);

    setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
    doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.clientInfo);
    doc.setFont('helvetica', 'normal');
    doc.text(`${clientName || 'N/A'}`, 20, y + 13);
    doc.text(`${companyName || 'N/A'}`, 20, y + 18);
    doc.text(`${clientEmail || 'N/A'}`, 20, y + 23);
    doc.text(`${new Date().toLocaleDateString()}`, 20, y + 28);
    doc.text(`${clientType === 'b2b' ? 'B2B' : clientType.charAt(0).toUpperCase() + clientType.slice(1)}`, 20, y + 33);

    y += 44;

    y = renderPageTitle(doc, 'INVESTMENT SUMMARY', y, primaryRgb);

    const hasSetupFeeWaived = discount && discount.waive_setup && selectedAdPlatforms.length > 0;
    const setupFeeSavings = hasSetupFeeWaived ? getAdditionalPrice('setup_fee') : 0;
    const hasWebDiscount = webInfo.discount > 0;
    const hasDiscounts = (marketingFees.disc > 0) || hasSetupFeeWaived || hasWebDiscount;

    // Calculate the exact height needed for the investment summary box
    let summaryHeight = 8; // Initial padding

    // Website Services line
    if (includeWeb) {
      summaryHeight += hasWebDiscount ? 11 : 6;
    }

    if (includeMarketing) {
      // Monthly Retainer: 6 for normal, or 11 if discount (5 for strikethrough + 6 for discounted)
      summaryHeight += marketingFees.disc > 0 ? 11 : 6;

      // Setup Fee: 6 for normal, or 11 if waived (5 for strikethrough + 6 for $0), or 0 if not shown
      if (marketingFees.setup > 0 || hasSetupFeeWaived) {
        summaryHeight += hasSetupFeeWaived ? 11 : 6;
      }

      // SAVINGS section
      if (hasDiscounts) {
        summaryHeight += 4; // spacing before SAVINGS
        summaryHeight += 5; // SAVINGS header
        if (hasWebDiscount) summaryHeight += 6; // Website Discount line
        if (marketingFees.disc > 0) summaryHeight += 6; // Monthly Discount line
        if (hasSetupFeeWaived) summaryHeight += 6; // Setup Fee Waived line
        summaryHeight += 6; // Total Savings line
      }
    } else if (hasWebDiscount) {
      // If only web discount (no marketing)
      summaryHeight += 4; // spacing before SAVINGS
      summaryHeight += 5; // SAVINGS header
      summaryHeight += 6; // Website Discount line
      summaryHeight += 6; // Total Savings line
    }

    // Final padding before the green "TOTAL DUE NOW" box
    summaryHeight += 4;

    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(PDF_TYPOGRAPHY.colors.border.r, PDF_TYPOGRAPHY.colors.border.g, PDF_TYPOGRAPHY.colors.border.b);
    doc.setLineWidth(0.3);
    doc.roundedRect(PDF_TYPOGRAPHY.margins.page, y, 180, summaryHeight, 2, 2, 'FD');

    y += 8;

    if (includeWeb) {
      setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
      doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.summaryItem);
      doc.setFont('helvetica', 'normal');
      doc.text('Website Services:', PDF_TYPOGRAPHY.margins.contentLeft, y);
      doc.setFont('helvetica', 'bold');

      if (hasWebDiscount) {
        setColor(doc, PDF_TYPOGRAPHY.colors.strikethrough);
        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.strikethroughPrice);
        doc.setFont('helvetica', 'normal');
        doc.text(`${fmt(webInfo.originalTotal)}`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
        doc.setDrawColor(PDF_TYPOGRAPHY.colors.strikethrough.r, PDF_TYPOGRAPHY.colors.strikethrough.g, PDF_TYPOGRAPHY.colors.strikethrough.b);
        doc.setLineWidth(0.5);
        const strikeWidth = doc.getTextWidth(`${fmt(webInfo.originalTotal)}`);
        doc.line(PDF_TYPOGRAPHY.margins.contentRight - strikeWidth, y - 1.5, PDF_TYPOGRAPHY.margins.contentRight, y - 1.5);
        setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
        y += 6;

        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.summaryPrice);
        doc.setFont('helvetica', 'bold');
        setColor(doc, PDF_TYPOGRAPHY.colors.green);
        doc.text(`${fmt(webInfo.total)}`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
        setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
      } else {
        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.summaryPrice);
        doc.text(`${fmt(webInfo.total)}`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
      }
      y += 7;
    }

    if (includeMarketing) {
      const monthlyBeforeDiscount = marketingFees.monthly + marketingFees.disc;

      doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.summaryItem);
      doc.setFont('helvetica', 'normal');
      doc.text('Monthly Retainer:', PDF_TYPOGRAPHY.margins.contentLeft, y);
      doc.setFont('helvetica', 'bold');

      if (marketingFees.disc > 0) {
        setColor(doc, PDF_TYPOGRAPHY.colors.strikethrough);
        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.strikethroughPrice);
        doc.setFont('helvetica', 'normal');
        doc.text(`${fmt(monthlyBeforeDiscount)}/mo`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
        doc.setDrawColor(PDF_TYPOGRAPHY.colors.strikethrough.r, PDF_TYPOGRAPHY.colors.strikethrough.g, PDF_TYPOGRAPHY.colors.strikethrough.b);
        doc.setLineWidth(0.5);
        const strikeWidth = doc.getTextWidth(`${fmt(monthlyBeforeDiscount)}/mo`);
        doc.line(PDF_TYPOGRAPHY.margins.contentRight - strikeWidth, y - 1.5, PDF_TYPOGRAPHY.margins.contentRight, y - 1.5);
        setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
        y += 6;

        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.summaryPrice);
        doc.setFont('helvetica', 'bold');
        setColor(doc, PDF_TYPOGRAPHY.colors.green);
        doc.text(`${fmt(marketingFees.monthly)}/mo`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
        setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
      } else {
        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.summaryPrice);
        doc.setFont('helvetica', 'bold');
        doc.text(`${fmt(marketingFees.monthly)}/mo`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
      }
      y += 7;

      if (marketingFees.setup > 0 || hasSetupFeeWaived) {
        const setupFeeAmount = getAdditionalPrice('setup_fee');
        doc.setFont('helvetica', 'normal');
        doc.text('Setup Fee:', PDF_TYPOGRAPHY.margins.contentLeft, y);

        if (hasSetupFeeWaived) {
          setColor(doc, PDF_TYPOGRAPHY.colors.strikethrough);
          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.strikethroughPrice);
          doc.setFont('helvetica', 'normal');
          doc.text(`${fmt(setupFeeAmount)}`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
          doc.setDrawColor(PDF_TYPOGRAPHY.colors.strikethrough.r, PDF_TYPOGRAPHY.colors.strikethrough.g, PDF_TYPOGRAPHY.colors.strikethrough.b);
          doc.setLineWidth(0.5);
          const strikeWidth = doc.getTextWidth(`${fmt(setupFeeAmount)}`);
          doc.line(PDF_TYPOGRAPHY.margins.contentRight - strikeWidth, y - 1.5, PDF_TYPOGRAPHY.margins.contentRight, y - 1.5);
          setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
          y += 6;

          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.summaryPrice);
          doc.setFont('helvetica', 'bold');
          setColor(doc, PDF_TYPOGRAPHY.colors.green);
          doc.text(`$0`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
          setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
        } else {
          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.summaryPrice);
          doc.setFont('helvetica', 'bold');
          doc.text(`${fmt(marketingFees.setup)}`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
        }
        y += 7;
      }

      if (hasDiscounts) {
        y += 4;

        setColor(doc, PDF_TYPOGRAPHY.colors.green);
        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.savingsLabel);
        doc.setFont('helvetica', 'bold');
        doc.text('SAVINGS:', PDF_TYPOGRAPHY.margins.contentLeft, y);
        y += 5;

        if (hasWebDiscount) {
          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.small);
          doc.setFont('helvetica', 'normal');
          const discountLabel = appliedWebDiscount?.code ? `Website Discount (${appliedWebDiscount.code}):` : 'Website Discount:';
          doc.text(discountLabel, PDF_TYPOGRAPHY.margins.indent, y);
          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.h3);
          doc.setFont('helvetica', 'bold');
          doc.text(`-${fmt(webInfo.discount)}`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
          y += 6;
        }

        if (marketingFees.disc > 0) {
          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.small);
          doc.setFont('helvetica', 'normal');
          const discountLabel = discount?.code ? `Marketing Discount (${discount.code}):` : 'Marketing Discount:';
          doc.text(discountLabel, PDF_TYPOGRAPHY.margins.indent, y);
          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.h3);
          doc.setFont('helvetica', 'bold');
          doc.text(`-${fmt(marketingFees.disc)}/mo`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
          y += 6;
        }

        if (hasSetupFeeWaived) {
          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.small);
          doc.setFont('helvetica', 'normal');
          doc.text(`Setup Fee Waived:`, PDF_TYPOGRAPHY.margins.indent, y);
          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.h3);
          doc.setFont('helvetica', 'bold');
          doc.text(`-${fmt(setupFeeSavings)}`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
          y += 6;
        }

        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.small);
        doc.setFont('helvetica', 'bold');
        const totalSavings = webInfo.discount + marketingFees.disc + setupFeeSavings;
        doc.text(`Total Savings:`, PDF_TYPOGRAPHY.margins.indent, y);
        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.h3);
        doc.text(`-${fmt(totalSavings)}`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
        y += 6;

        setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
      }
    }

    // Handle website-only discount (no marketing)
    if (!includeMarketing && hasWebDiscount) {
      y += 4;

      setColor(doc, PDF_TYPOGRAPHY.colors.green);
      doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.savingsLabel);
      doc.setFont('helvetica', 'bold');
      doc.text('SAVINGS:', PDF_TYPOGRAPHY.margins.contentLeft, y);
      y += 5;

      doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.small);
      doc.setFont('helvetica', 'normal');
      const discountLabel = appliedWebDiscount?.code ? `Website Discount (${appliedWebDiscount.code}):` : 'Website Discount:';
      doc.text(discountLabel, PDF_TYPOGRAPHY.margins.indent, y);
      doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.h3);
      doc.setFont('helvetica', 'bold');
      doc.text(`-${fmt(webInfo.discount)}`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
      y += 6;

      doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.small);
      doc.setFont('helvetica', 'bold');
      doc.text(`Total Savings:`, PDF_TYPOGRAPHY.margins.indent, y);
      doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.h3);
      doc.text(`-${fmt(webInfo.discount)}`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
      y += 6;

      setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
    }

    y += 6;

    if (isWebDesignMiami) {
      doc.setFillColor(primaryRgb.r, primaryRgb.g, primaryRgb.b);
      doc.roundedRect(PDF_TYPOGRAPHY.margins.page, y, 180, 20, 2, 2, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.totalDueNow);
      doc.setFont('helvetica', 'bold');
      doc.text(`TOTAL DUE NOW:`, PDF_TYPOGRAPHY.margins.contentLeft, y + 12);
      doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.totalDueNowAmount);
      doc.text(`${fmt(webInfo.total + marketingFees.first)}`, PDF_TYPOGRAPHY.margins.contentRight, y + 12, { align: 'right' });
    } else {
      doc.setFillColor(accentColor.r, accentColor.g, accentColor.b);
      doc.roundedRect(PDF_TYPOGRAPHY.margins.page, y, 180, 20, 2, 2, 'F');

      setColor(doc, darkTeal);
      doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.totalDueNow);
      doc.setFont('helvetica', 'bold');
      doc.text(`TOTAL DUE NOW:`, PDF_TYPOGRAPHY.margins.contentLeft, y + 12);
      doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.totalDueNowAmount);
      doc.text(`${fmt(webInfo.total + marketingFees.first)}`, PDF_TYPOGRAPHY.margins.contentRight, y + 12, { align: 'right' });
    }

    y += 24;

    if (includeMarketing && marketingFees.monthly > 0) {
      setColor(doc, PDF_TYPOGRAPHY.colors.textMuted);
      doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.thenMonthly);
      doc.setFont('helvetica', 'italic');
      doc.text(`Then ${fmt(marketingFees.monthly + adSpend)}/month thereafter`, PDF_TYPOGRAPHY.margins.page, y);
      y += 6;
    }

    y = checkPageBreak(doc, y, 40);
    if (y === 20) {
      y = 20;
    } else {
      y += PDF_TYPOGRAPHY.spacing.subsection;
    }

    y = renderPageTitle(doc, 'DETAILED BREAKDOWN', y, primaryRgb);

    setColor(doc, PDF_TYPOGRAPHY.colors.textPrimary);
    doc.setFont('helvetica', 'normal');

    if (includeWeb) {
      y = renderSectionHeader(doc, 'Website Package', y, primaryRgb);

      const pkgService = getServicesByCategory('website_packages').find(s => s.tier_key === pkg);
      if (pkgService) {
        y = renderServiceItem(
          doc,
          pkgService.name,
          fmt(pkgService.price),
          y,
          pkgService.description,
          null,
          null
        );

        if (pkgService.payment_schedule && pkgService.payment_schedule.length > 0) {
          y = checkPageBreak(doc, y);
          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.body);
          doc.setFont('helvetica', 'bold');
          setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
          doc.text('Payment Schedule:', PDF_TYPOGRAPHY.margins.contentLeft + 3, y);
          doc.setFont('helvetica', 'normal');
          y += PDF_TYPOGRAPHY.spacing.itemGap;
          pkgService.payment_schedule.forEach(milestone => {
            doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.description);
            doc.text(`  ${milestone.percentage}%  ${milestone.description}`, 30, y);
            y += PDF_TYPOGRAPHY.lineHeights.list;
          });
          y += PDF_TYPOGRAPHY.spacing.bullet;
        }

        if (pkgService.key_deliverables && pkgService.key_deliverables.length > 0) {
          y = checkPageBreak(doc, y);
          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.body);
          doc.setFont('helvetica', 'bold');
          setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
          doc.text('Key Deliverables:', PDF_TYPOGRAPHY.margins.contentLeft + 3, y);
          doc.setFont('helvetica', 'normal');
          y += PDF_TYPOGRAPHY.spacing.itemGap;
          pkgService.key_deliverables.forEach(deliverable => {
            doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.description);
            const split = doc.splitTextToSize(`• ${deliverable}`, PDF_TYPOGRAPHY.maxWidths.description);
            doc.text(split, 30, y);
            y += split.length * PDF_TYPOGRAPHY.lineHeights.list;
          });
          y += PDF_TYPOGRAPHY.spacing.bullet;
        }

        if (pkgService.features) {
          y = checkPageBreak(doc, y);
          doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.body);
          doc.setFont('helvetica', 'bold');
          setColor(doc, PDF_TYPOGRAPHY.colors.textSecondary);
          doc.text('Features Included:', PDF_TYPOGRAPHY.margins.contentLeft + 3, y);
          doc.setFont('helvetica', 'normal');
          y += PDF_TYPOGRAPHY.spacing.itemGap;
          y = renderFeaturesToPDF(doc, pkgService.features, y, PDF_TYPOGRAPHY.maxWidths.description);
          y += PDF_TYPOGRAPHY.spacing.bullet;
        }

        y += PDF_TYPOGRAPHY.spacing.lineItem;
      }

      if (addOns.length > 0 || customPages > 0 || sameLayoutPages > 0 || extraRevisions > 0 || customWebHours > 0) {
        y += PDF_TYPOGRAPHY.spacing.beforeSection;
        y = checkPageBreak(doc, y);
        doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.body);
        doc.setFont('helvetica', 'bold');
        setColor(doc, darkTeal);
        doc.text('Additional Items:', PDF_TYPOGRAPHY.margins.contentLeft, y);
        doc.setFont('helvetica', 'normal');
        setColor(doc, PDF_TYPOGRAPHY.colors.textPrimary);
        y += PDF_TYPOGRAPHY.spacing.itemGap;

        addOns.forEach(id => {
          const addon = getServicesByCategory('web_addons').find(s => s.tier_key === id);
          if (addon) {
            y = checkPageBreak(doc, y);
            y = renderServiceItem(
              doc,
              addon.name,
              fmt(addon.price),
              y,
              addon.description,
              addon.features,
              (features, startY, maxWidth) => renderFeaturesToPDF(doc, features, startY, maxWidth)
            );
          }
        });

        if (customPages > 0) {
          y = renderAdditionalItem(
            doc,
            `Custom Pages (${customPages} x ${fmt(getAdditionalPrice('custom_page'))})`,
            fmt(customPages * getAdditionalPrice('custom_page')),
            y
          );
        }

        if (sameLayoutPages > 0) {
          y = renderAdditionalItem(
            doc,
            `Same Layout Pages (${sameLayoutPages} x ${fmt(getAdditionalPrice('same_layout_page'))})`,
            fmt(sameLayoutPages * getAdditionalPrice('same_layout_page')),
            y
          );
        }

        if (extraRevisions > 0) {
          y = renderAdditionalItem(
            doc,
            `Extra Revisions (${extraRevisions} x ${fmt(getAdditionalPrice('extra_revision'))})`,
            fmt(extraRevisions * getAdditionalPrice('extra_revision')),
            y
          );
        }

        if (customWebHours > 0) {
          y = renderAdditionalItem(
            doc,
            `Custom Hours (${customWebHours} x ${fmt(getAdditionalPrice('hourly_rate'))})`,
            fmt(customWebHours * getAdditionalPrice('hourly_rate')),
            y,
            customWebDesc
          );
        }
      }

      y += PDF_TYPOGRAPHY.spacing.afterSection;
      doc.setDrawColor(primaryRgb.r, primaryRgb.g, primaryRgb.b);
      doc.setLineWidth(0.5);
      doc.line(20, y, 190, y);
      y += PDF_TYPOGRAPHY.spacing.itemGap;
      doc.setFontSize(PDF_TYPOGRAPHY.fontSizes.h2);
      doc.setFont('helvetica', 'bold');
      setColor(doc, darkTeal);
      doc.text(`Website Total:`, PDF_TYPOGRAPHY.margins.contentLeft, y);
      doc.text(`${fmt(webInfo.total)}`, PDF_TYPOGRAPHY.margins.contentRight, y, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      setColor(doc, PDF_TYPOGRAPHY.colors.textPrimary);
      y += PDF_TYPOGRAPHY.spacing.servicePackage;
    }

    if (includeMarketing) {
      y = checkPageBreak(doc, y, 50);

      y = renderSectionHeader(doc, 'Marketing Services', y, primaryRgb);

      if (selectedSupportPlan) {
        const plan = supportPlans.find(p => p.tier_key === selectedSupportPlan);
        if (plan) {
          y = checkPageBreak(doc, y);

          const hoursText = `${plan.hours} hours/month`;
          const fullDesc = plan.description ? `${hoursText}\n\n${plan.description}` : hoursText;

          y = renderServiceItem(
            doc,
            plan.name,
            `${fmt(plan.price)}/mo`,
            y,
            fullDesc,
            plan.features,
            (features, startY, maxWidth) => renderFeaturesToPDF(doc, features, startY, maxWidth)
          );
        }
      }

      if (seoTier) {
        const seo = getServicesByCategory('seo_services', clientType).find(s => s.tier_key === seoTier);
        if (seo) {
          y = checkPageBreak(doc, y);
          y = renderServiceItem(
            doc,
            seo.name,
            `${fmt(seo.price)}/mo`,
            y,
            seo.description,
            seo.features,
            (features, startY, maxWidth) => renderFeaturesToPDF(doc, features, startY, maxWidth)
          );
        }
      }

      if (emailTier) {
        const email = getServicesByCategory('email_services', clientType).find(s => s.tier_key === emailTier);
        if (email) {
          y = checkPageBreak(doc, y);
          y = renderServiceItem(
            doc,
            email.name,
            `${fmt(email.price)}/mo`,
            y,
            email.description,
            email.features,
            (features, startY, maxWidth) => renderFeaturesToPDF(doc, features, startY, maxWidth)
          );
        }
      }

      if (selectedAdPlatforms.length > 0) {
        const adServices = getServicesByCategory('ad_platforms', clientType);
        selectedAdPlatforms.forEach((platform, index) => {
          const ad = adServices.find(s => s.tier_key === platform);
          if (ad) {
            y = checkPageBreak(doc, y);
            const adPrice = index === 0 ? ad.price : (ad.additional_price || ad.price);
            y = renderServiceItem(
              doc,
              ad.name,
              `${fmt(adPrice)}/mo`,
              y,
              ad.description,
              ad.features,
              (features, startY, maxWidth) => renderFeaturesToPDF(doc, features, startY, maxWidth)
            );
          }
        });

        y = checkPageBreak(doc, y);
        const adManagementFee = adSpend * 0.10;

        y = renderAdditionalItem(
          doc,
          `Ad Management (10% of ${fmt(adSpend)})`,
          `${fmt(adManagementFee)}/mo`,
          y
        );
        y = renderAdditionalItem(
          doc,
          `Ad Spend Budget`,
          `${fmt(adSpend)}/mo`,
          y
        );
        y += PDF_TYPOGRAPHY.spacing.subItemGap;
      }

      if (clientType === 'ecommerce' && croTier) {
        const cro = getServicesByCategory('cro_services').find(s => s.tier_key === croTier);
        if (cro) {
          y = checkPageBreak(doc, y);
          y = renderServiceItem(
            doc,
            cro.name,
            `${fmt(cro.price)}/mo`,
            y,
            cro.description,
            cro.features,
            (features, startY, maxWidth) => renderFeaturesToPDF(doc, features, startY, maxWidth)
          );
        }
      }

      if (multiLocation > 0) {
        y = renderAdditionalItem(
          doc,
          `Multi-Location (${multiLocation} x ${fmt(getAdditionalPrice('multi_location'))})`,
          `${fmt(multiLocation * getAdditionalPrice('multi_location'))}/mo`,
          y
        );
      }

      if (customMarketingHours > 0) {
        y = renderAdditionalItem(
          doc,
          `Custom Hours (${customMarketingHours} x ${fmt(getAdditionalPrice('hourly_rate'))})`,
          `${fmt(customMarketingHours * getAdditionalPrice('hourly_rate'))}/mo`,
          y,
          customMarketingDesc
        );
      }
    }

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);

      doc.setTextColor(0, 0, 0);
    }

    doc.save(`estimate-${clientName || 'draft'}-${new Date().toISOString().split('T')[0]}.pdf`);
  }

  const webInfo = calcWeb();
  const marketingFees = calcMarketing();

  const packages = getServicesByCategory('website_packages');
  const webAddOns = getServicesByCategory('web_addons');
  const seoServices = getServicesByCategory('seo_services', clientType);
  const emailServices = getServicesByCategory('email_services', clientType);

  const adPlatforms = getServicesByCategory('ad_platforms', clientType).sort((a, b) => {
    const order = ['google', 'meta', 'tiktok', 'linkedin'];
    const aIndex = order.indexOf(a.tier_key);
    const bIndex = order.indexOf(b.tier_key);
    return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
  });

  const croServices = getServicesByCategory('cro_services');

  if (pricingLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  const getBrandLogoStyle = () => {
    if (!selectedBrand) return {};

    return {
      padding: '8px 16px'
    };
  };

  const getBrandFont = () => {
    return { fontFamily: '"Inter", -apple-system, sans-serif', fontWeight: 600 };
  };

  const isWebDesignMiami = () => {
    return selectedBrand?.name === 'Web Design Miami Company' ||
           selectedBrand?.name === 'West Palm Beach Web Design Company';
  };

  const getActiveClasses = () => {
    if (isWebDesignMiami()) {
      return {
        border: `2px solid #1F2937`,
        backgroundColor: `#F3F4F6`
      };
    }
    const primary = selectedBrand?.primary_color || '#3B82F6';
    return {
      border: `2px solid ${primary}`,
      backgroundColor: `${primary}10`
    };
  };

  const getButtonClasses = () => {
    if (isWebDesignMiami()) {
      return {
        backgroundColor: '#1F2937',
        color: '#FFFFFF'
      };
    }
    const primary = selectedBrand?.primary_color || '#3B82F6';
    return {
      backgroundColor: primary,
      color: '#FFFFFF'
    };
  };

  const getAccentTextColor = () => {
    if (isWebDesignMiami()) {
      return { color: '#1F2937' };
    }
    return { color: selectedBrand?.primary_color || '#3B82F6' };
  };

  const getAccentBgClasses = () => {
    if (isWebDesignMiami()) {
      return {
        backgroundColor: `#F3F4F6`,
        color: '#1F2937'
      };
    }
    const primary = selectedBrand?.primary_color || '#3B82F6';
    return {
      backgroundColor: `${primary}15`,
      color: primary
    };
  };

  const getBrandGradient = () => {
    if (isWebDesignMiami()) {
      return '#FFFFFF';
    }
    if (!selectedBrand) {
      return 'linear-gradient(135deg, #3B82F6 0%, #3B82F6 100%)';
    }
    const primary = selectedBrand.primary_color;
    const secondary = selectedBrand.secondary_color || primary;
    return `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`;
  };

  const getBrandGradientSubtle = () => {
    if (isWebDesignMiami()) {
      return `#F9FAFB`;
    }
    const primary = selectedBrand?.primary_color || '#3B82F6';
    const secondary = selectedBrand?.secondary_color || primary;
    return `linear-gradient(135deg, ${primary}15 0%, ${secondary}15 100%)`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div
        className={`w-full py-6 sm:py-8 px-4 sm:px-6 mb-6 ${isWebDesignMiami() ? 'shadow-md border-b border-gray-200' : 'shadow-lg'}`}
        style={{ background: getBrandGradient() }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              {selectedBrand?.logo_url && (
                <div style={getBrandLogoStyle()}>
                  <img
                    src={selectedBrand.logo_url}
                    alt={selectedBrand.name}
                    className="h-10 sm:h-12 object-contain"
                  />
                </div>
              )}
              {brands.length > 1 && (
                <div className="min-w-[280px]">
                  <CustomSelect
                    options={brands.map(brand => ({ value: brand.id, label: brand.name }))}
                    value={selectedBrand?.id || ''}
                    onChange={(brandId) => {
                      const brand = brands.find(b => b.id === brandId);
                      setSelectedBrand(brand || null);
                    }}
                    className={
                      isWebDesignMiami()
                        ? 'bg-gradient-to-r from-gray-50 to-white border-2 border-gray-200 text-gray-900 hover:border-gray-300 focus:ring-gray-400'
                        : 'bg-gradient-to-r from-white/95 to-white/90 backdrop-blur-lg border-2 border-white/20 text-gray-900 hover:border-white/30 focus:ring-white/50'
                    }
                  />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium ${isWebDesignMiami() ? 'text-gray-900' : 'text-white/90'}`}>
                {profile?.full_name}
              </span>
              {(profile?.role === 'admin' || profile?.role === 'manager') && (
                <button
                  onClick={() => setShowAdmin(true)}
                  className={`p-2 rounded-lg transition-colors ${
                    isWebDesignMiami()
                      ? 'bg-gray-100 hover:bg-gray-200'
                      : 'bg-white/20 hover:bg-white/30'
                  }`}
                >
                  <Settings className={`w-5 h-5 ${isWebDesignMiami() ? 'text-gray-700' : 'text-white'}`} />
                </button>
              )}
              <button
                onClick={signOut}
                className={`p-2 rounded-lg transition-colors ${
                  isWebDesignMiami()
                    ? 'bg-gray-100 hover:bg-gray-200'
                    : 'bg-white/20 hover:bg-white/30'
                }`}
              >
                <LogOut className={`w-5 h-5 ${isWebDesignMiami() ? 'text-gray-700' : 'text-white'}`} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8">

        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md mb-4 sm:mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-3 text-gray-700">Client Name *</label>
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl transition-all outline-none ${
                  isWebDesignMiami()
                    ? 'focus:border-gray-400 focus:ring-2 focus:ring-gray-200'
                    : 'focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
                }`}
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-3 text-gray-700">Company Name</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl transition-all outline-none ${
                  isWebDesignMiami()
                    ? 'focus:border-gray-400 focus:ring-2 focus:ring-gray-200'
                    : 'focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
                }`}
                placeholder="Acme Inc."
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-3 text-gray-700">Email *</label>
              <input
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl transition-all outline-none ${
                  isWebDesignMiami()
                    ? 'focus:border-gray-400 focus:ring-2 focus:ring-gray-200'
                    : 'focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
                }`}
                placeholder="john@example.com"
              />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md mb-4 sm:mb-6">
          <button
            onClick={() => setIndustryCollapsed(!industryCollapsed)}
            className="w-full flex items-center justify-between text-left hover:opacity-80 transition-opacity"
          >
            <h2 className="text-lg sm:text-xl font-bold text-gray-800">Industry</h2>
            {industryCollapsed ? (
              <ChevronDown className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronUp className="w-5 h-5 text-gray-600" />
            )}
          </button>
          {!industryCollapsed && (
            <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-4">
            {(['local', 'ecommerce', 'b2b'] as const).map(t => {
              const isSelected = clientType === t;
              return (
                <button
                  key={t}
                  onClick={() => setClientType(t)}
                  className="p-3 sm:p-4 rounded-xl border-2 transition-all transform hover:scale-105"
                  style={isSelected ? {
                    ...getActiveClasses(),
                    background: getBrandGradientSubtle(),
                    boxShadow: isWebDesignMiami()
                      ? `0 4px 12px rgba(31, 41, 55, 0.1)`
                      : `0 4px 12px ${selectedBrand?.primary_color || '#3B82F6'}20`
                  } : { border: '2px solid #E5E7EB' }}
                >
                  <div style={isSelected ? getAccentTextColor() : { color: '#6b7280' }}>
                    {t === 'local' && <Building2 className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-1" />}
                    {t === 'ecommerce' && <ShoppingCart className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-1" />}
                    {t === 'b2b' && <Briefcase className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-1" />}
                    <div className="font-bold text-xs sm:text-sm">
                      {t === 'b2b' ? 'B2B' : t.charAt(0).toUpperCase() + t.slice(1)}
                    </div>
                  </div>
                </button>
              );
            })}
            </div>
          )}
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-xl shadow-md mb-4 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-bold mb-4 text-gray-800">Services</h2>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <label className="flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer hover:bg-gray-50 transition-all flex-1">
              <input
                type="checkbox"
                checked={includeWeb}
                onChange={(e) => setIncludeWeb(e.target.checked)}
                className={`w-5 h-5 ${isWebDesignMiami() ? 'accent-gray-800' : 'accent-blue-600'}`}
              />
              <span className="font-semibold text-gray-700">Website Services</span>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer hover:bg-gray-50 transition-all flex-1">
              <input
                type="checkbox"
                checked={includeMarketing}
                onChange={(e) => setIncludeMarketing(e.target.checked)}
                className={`w-5 h-5 ${isWebDesignMiami() ? 'accent-gray-800' : 'accent-blue-600'}`}
              />
              <span className="font-semibold text-gray-700">Marketing Services</span>
            </label>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr,400px] gap-6 max-w-7xl mx-auto">
          <div className="space-y-6">
            {includeWeb && (
              <>
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-xl font-bold mb-4">Website Package</h3>
                  <div className="grid grid-cols-1 gap-3">
                    {packages.map(p => (
                      <div key={p.tier_key}>
                        <button
                          onClick={() => setPkg(p.tier_key)}
                          className="w-full p-4 rounded-lg border-2 text-left transition-all"
                          style={pkg === p.tier_key ? getActiveClasses() : { border: '2px solid #E5E7EB' }}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="font-bold text-lg">{p.name}</div>
                              {p.description && (
                                <div className="text-sm text-gray-600 mt-1">{p.description}</div>
                              )}
                            </div>
                            <div className="text-xl font-bold ml-4" style={getAccentTextColor()}>{fmt(p.price)}</div>
                          </div>
                        </button>
                        {pkg === p.tier_key && ((p.features && (p.features.sections || Array.isArray(p.features))) || (p.key_deliverables && p.key_deliverables.length > 0)) && (
                          <div className="mt-2">
                            <button
                              onClick={() => setExpandedPackage(expandedPackage === p.tier_key ? null : p.tier_key)}
                              className="w-full px-4 py-2 rounded-lg flex justify-between items-center text-sm font-medium hover:opacity-80 transition-colors"
                              style={getAccentBgClasses()}
                            >
                              <span>View Details</span>
                              {expandedPackage === p.tier_key ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                            {expandedPackage === p.tier_key && (
                              <div className="mt-2 p-4 bg-gray-50 rounded-lg border">
                                {p.features && (p.features.sections || Array.isArray(p.features)) && (
                                  <div className="mb-4">
                                    <h5 className="font-semibold text-sm mb-2">Features Included:</h5>
                                    {renderStructuredFeatures(p.features)}
                                  </div>
                                )}
                                {p.key_deliverables && p.key_deliverables.length > 0 && (
                                  <div className="mb-4">
                                    <h5 className="font-semibold text-sm mb-2">Key Deliverables:</h5>
                                    <ul className="space-y-1">
                                      {p.key_deliverables.map((deliverable, idx) => (
                                        <li key={idx} className="flex items-start gap-2 text-sm">
                                          <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isWebDesignMiami() ? 'text-gray-700' : 'text-blue-600'}`} />
                                          <span>{deliverable}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {p.payment_schedule && p.payment_schedule.length > 0 && (
                                  <div>
                                    <h5 className="font-semibold text-sm mb-2">Payment Schedule:</h5>
                                    <ul className="space-y-1">
                                      {p.payment_schedule.map((milestone, idx) => (
                                        <li key={idx} className="flex items-start gap-2 text-sm">
                                          <span className="font-semibold text-gray-700 min-w-[3rem]">{milestone.percentage}%</span>
                                          <span className="text-gray-600">{milestone.description}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-xl font-bold mb-4">Add-Ons</h3>
                  <div className="grid grid-cols-1 gap-3">
                    {webAddOns.map(a => (
                      <button
                        key={a.tier_key}
                        onClick={() => setAddOns(addOns.includes(a.tier_key) ? addOns.filter(x => x !== a.tier_key) : [...addOns, a.tier_key])}
                        className="w-full p-4 rounded-lg border-2 text-left transition-all"
                        style={addOns.includes(a.tier_key) ? getActiveClasses() : { border: '2px solid #E5E7EB' }}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-bold">{a.name}</div>
                            {a.show_description !== false && a.description && (
                              <div className="text-sm text-gray-600 mt-1">{a.description}</div>
                            )}
                          </div>
                          <div className="font-bold ml-4" style={getAccentTextColor()}>{fmt(a.price)}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-xl font-bold mb-4">Additional Pages & Revisions</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Custom Pages ({fmt(getAdditionalPrice('custom_page'))} each)
                      </label>
                      <input
                        type="number"
                        value={customPages}
                        onChange={(e) => setCustomPages(parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Same Layout Pages ({fmt(getAdditionalPrice('same_layout_page'))} each)
                      </label>
                      <input
                        type="number"
                        value={sameLayoutPages}
                        onChange={(e) => setSameLayoutPages(parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Extra Revisions ({fmt(getAdditionalPrice('extra_revision'))} each)
                      </label>
                      <input
                        type="number"
                        value={extraRevisions}
                        onChange={(e) => setExtraRevisions(parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 border rounded-lg"
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-xl font-bold mb-4">Custom Hours</h3>
                  <input
                    type="number"
                    value={customWebHours}
                    onChange={(e) => setCustomWebHours(parseInt(e.target.value) || 0)}
                    className="w-full border-2 px-3 py-2 rounded-lg mb-2"
                    placeholder="Hours"
                  />
                  <textarea
                    value={customWebDesc}
                    onChange={(e) => setCustomWebDesc(e.target.value)}
                    className="w-full border-2 px-3 py-2 rounded-lg"
                    rows={2}
                    placeholder="Description"
                  />
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-xl font-bold mb-4">Website Discount</h3>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={webDiscountCode}
                      onChange={(e) => setWebDiscountCode(e.target.value.toUpperCase())}
                      placeholder="Code"
                      className="flex-1 border-2 px-3 py-2 rounded-lg uppercase"
                      disabled={appliedWebDiscount !== null}
                    />
                    <button
                      onClick={handleApplyWebDiscount}
                      className="px-4 py-2 rounded-lg text-white"
                      style={appliedWebDiscount ? { backgroundColor: '#EF4444' } : getButtonClasses()}
                    >
                      {appliedWebDiscount ? 'Remove' : 'Apply'}
                    </button>
                  </div>
                  {webDiscountError && <p className="text-red-500 text-sm mt-2">{webDiscountError}</p>}
                  {appliedWebDiscount && (
                    <div className="mt-2 p-2 bg-green-50 rounded-lg text-green-800 text-sm flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      {appliedWebDiscount.type === 'percentage' ? `${appliedWebDiscount.value}% off` : `$${appliedWebDiscount.value} off`} applied
                    </div>
                  )}
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-xl font-bold mb-4">Website Care & Development Plans</h3>
                  <div className="space-y-2">
                    {supportPlans.map(plan => (
                      <div key={plan.tier_key}>
                        <button
                          onClick={() => setSelectedSupportPlan(selectedSupportPlan === plan.tier_key ? null : plan.tier_key)}
                          className="w-full p-4 rounded-lg border-2 text-left transition-all"
                          style={selectedSupportPlan === plan.tier_key ? getActiveClasses() : { border: '2px solid #E5E7EB' }}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div className="font-bold text-lg">{plan.name}</div>
                            <div className="font-bold text-xl" style={getAccentTextColor()}>{fmt(plan.price)}/mo</div>
                          </div>
                          <div className="text-sm text-gray-600">{plan.hours} hours included per month</div>
                          {plan.description && (
                            <div className="text-sm text-gray-700 mt-2">{plan.description}</div>
                          )}
                        </button>

                        {selectedSupportPlan === plan.tier_key && plan.features && (
                          (plan.features.sections && Array.isArray(plan.features.sections) && plan.features.sections.length > 0) ||
                          (Array.isArray(plan.features) && plan.features.length > 0)
                        ) && (
                          <div className="mt-2 ml-4">
                            <button
                              onClick={() => setExpandedService(expandedService === plan.tier_key ? null : plan.tier_key)}
                              className={`w-full px-4 py-2 rounded-lg flex justify-between items-center text-sm font-medium transition-colors ${
                                isWebDesignMiami()
                                  ? 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                              }`}
                            >
                              <span>View Details</span>
                              {expandedService === plan.tier_key ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>

                            {expandedService === plan.tier_key && (
                              <div className="mt-2 p-4 bg-gray-50 rounded-lg border space-y-4">
                                {(plan.features.sections || plan.features).map((feature, idx) => (
                                  <div key={idx}>
                                    <h5 className="font-semibold text-sm mb-1">{feature.title}</h5>
                                    {feature.subtitle && (
                                      <p className="text-sm text-gray-600 mb-2">{feature.subtitle}</p>
                                    )}
                                    {feature.items && feature.items.length > 0 && (
                                      <ul className="space-y-1">
                                        {feature.items.map((item, itemIdx) => (
                                          <li key={itemIdx} className="text-sm text-gray-700 flex items-start gap-2">
                                            <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                            <span>{item}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                ))}

                                {plan.turnaround_times && (
                                  <div>
                                    <h5 className="font-semibold text-sm mb-2">Turnaround Times</h5>
                                    <div className="text-sm text-gray-700 space-y-1">
                                      {plan.turnaround_times.simple && (
                                        <div className="flex items-start gap-2">
                                          <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                          <span><strong>Simple tasks:</strong> {plan.turnaround_times.simple}</span>
                                        </div>
                                      )}
                                      {plan.turnaround_times.moderate && (
                                        <div className="flex items-start gap-2">
                                          <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                          <span><strong>Moderate tasks:</strong> {plan.turnaround_times.moderate}</span>
                                        </div>
                                      )}
                                      {plan.turnaround_times.complex && (
                                        <div className="flex items-start gap-2">
                                          <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                          <span><strong>Complex tasks:</strong> {plan.turnaround_times.complex}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {includeMarketing && (
              <>
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-xl font-bold mb-2">Paid Ads</h3>
                  {selectedAdPlatforms.length > 0 && (
                    <div className={`mb-4 p-3 rounded-lg ${
                      isWebDesignMiami()
                        ? 'bg-gray-50 border border-gray-300'
                        : 'bg-blue-50 border border-blue-200'
                    }`}>
                      <p className={`text-sm ${isWebDesignMiami() ? 'text-gray-800' : 'text-blue-900'}`}>
                        {selectedAdPlatforms.length >= 1 && (
                          <>
                            1st platform {fmt(adPlatforms.find(p => p.tier_key === selectedAdPlatforms[0])?.price || 0)}
                            {selectedAdPlatforms.length >= 2 && (
                              <> + 2nd {fmt(adPlatforms.find(p => p.tier_key === selectedAdPlatforms[1])?.additional_price || adPlatforms.find(p => p.tier_key === selectedAdPlatforms[1])?.price || 0)}</>
                            )}
                            {selectedAdPlatforms.length >= 3 && (
                              <> + 3rd {fmt(adPlatforms.find(p => p.tier_key === selectedAdPlatforms[2])?.additional_price || adPlatforms.find(p => p.tier_key === selectedAdPlatforms[2])?.price || 0)}</>
                            )}
                            {' + 10% ad spend commission'}
                          </>
                        )}
                      </p>
                    </div>
                  )}
                  <label className="block text-sm font-medium mb-2">Monthly Ad Budget</label>
                  <input
                    type="number"
                    value={adSpend}
                    onChange={(e) => setAdSpend(parseInt(e.target.value) || 0)}
                    className="w-full border-2 px-3 py-2 rounded-lg mb-3"
                  />
                  <div className="grid grid-cols-1 gap-2">
                    {adPlatforms.map(p => (
                      <div key={p.tier_key}>
                        <label className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                          isWebDesignMiami() ? 'hover:border-gray-400' : 'hover:border-blue-300'
                        }`}>
                          <input
                            type="checkbox"
                            checked={selectedAdPlatforms.includes(p.tier_key)}
                            onChange={() => setSelectedAdPlatforms(
                              selectedAdPlatforms.includes(p.tier_key)
                                ? selectedAdPlatforms.filter(x => x !== p.tier_key)
                                : [...selectedAdPlatforms, p.tier_key]
                            )}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="font-semibold">{p.name}</div>
                            {p.show_description !== false && p.description && (
                              <div className="text-sm text-gray-600 mt-1">{p.description}</div>
                            )}
                          </div>
                        </label>
                        {selectedAdPlatforms.includes(p.tier_key) && (p.features?.sections || (p.features && p.features.length > 0) || (p.key_deliverables && p.key_deliverables.length > 0)) && (
                          <div className="mt-2 ml-4">
                            <button
                              onClick={() => setExpandedService(expandedService === p.tier_key ? null : p.tier_key)}
                              className={`w-full px-4 py-2 rounded-lg flex justify-between items-center text-sm font-medium transition-colors ${
                                isWebDesignMiami()
                                  ? 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                              }`}
                            >
                              <span>View Details</span>
                              {expandedService === p.tier_key ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                            {expandedService === p.tier_key && (
                              <div className="mt-2 p-4 bg-gray-50 rounded-lg border">
                                {p.features && p.features.sections ? (
                                  renderStructuredFeatures(p.features)
                                ) : (
                                  <>
                                    {p.description && (
                                      <div className="mb-4">
                                        {renderStructuredDescription(p.description)}
                                      </div>
                                    )}
                                    {p.features && Array.isArray(p.features) && p.features.length > 0 && (
                                      <div className="mb-4">
                                        <h5 className="font-semibold text-sm mb-2">Features Included:</h5>
                                        <ul className="space-y-1">
                                          {p.features.map((feature, idx) => (
                                            <li key={idx} className="flex items-start gap-2 text-sm">
                                              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                              <span>{feature}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                    {p.key_deliverables && p.key_deliverables.length > 0 && (
                                      <div>
                                        <h5 className="font-semibold text-sm mb-2">Key Deliverables:</h5>
                                        <ul className="space-y-1">
                                          {p.key_deliverables.map((deliverable, idx) => (
                                            <li key={idx} className="flex items-start gap-2 text-sm">
                                              <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isWebDesignMiami() ? 'text-gray-700' : 'text-blue-600'}`} />
                                              <span>{deliverable}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-xl font-bold mb-4">Email & SMS</h3>
                  <div className="grid grid-cols-1 gap-3">
                    {emailServices.map(s => (
                      <div key={s.tier_key}>
                        <button
                          onClick={() => setEmailTier(s.tier_key === emailTier ? null : s.tier_key)}
                          className="w-full p-4 rounded-lg border-2 text-left transition-all"
                          style={emailTier === s.tier_key ? getActiveClasses() : { border: '2px solid #E5E7EB' }}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="font-bold text-lg">{s.name}</div>
                              {s.show_description !== false && s.description && (
                                <div className="text-sm text-gray-600 mt-1">{s.description}</div>
                              )}
                            </div>
                            <div className="text-xl font-bold ml-4" style={getAccentTextColor()}>{fmt(s.price)}/mo</div>
                          </div>
                        </button>
                        {emailTier === s.tier_key && (s.features?.sections || s.features?.length > 0 || s.key_deliverables?.length > 0) && (
                          <div className="mt-2">
                            <button
                              onClick={() => setExpandedService(expandedService === s.tier_key ? null : s.tier_key)}
                              className="w-full px-4 py-2 rounded-lg flex justify-between items-center text-sm font-medium hover:opacity-80 transition-colors"
                              style={getAccentBgClasses()}
                            >
                              <span>View Details</span>
                              {expandedService === s.tier_key ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                            {expandedService === s.tier_key && (
                              <div className="mt-2 p-4 bg-gray-50 rounded-lg border">
                                {s.features && s.features.sections ? (
                                  renderStructuredFeatures(s.features)
                                ) : (
                                  <>
                                    {s.description && (
                                      <div className="mb-4">
                                        {renderStructuredDescription(s.description)}
                                      </div>
                                    )}
                                    {s.features && Array.isArray(s.features) && s.features.length > 0 && (
                                      <div className="mb-4">
                                        <h5 className="font-semibold text-sm mb-2">Features Included:</h5>
                                        <ul className="space-y-1">
                                          {s.features.map((feature, idx) => (
                                            <li key={idx} className="flex items-start gap-2 text-sm">
                                              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                              <span>{feature}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                    {s.key_deliverables && s.key_deliverables.length > 0 && (
                                      <div>
                                        <h5 className="font-semibold text-sm mb-2">Key Deliverables:</h5>
                                        <ul className="space-y-1">
                                          {s.key_deliverables.map((deliverable, idx) => (
                                            <li key={idx} className="flex items-start gap-2 text-sm">
                                              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                              <span>{deliverable}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-xl font-bold mb-4">SEO</h3>
                  <div className="grid grid-cols-1 gap-3">
                    {seoServices.map(s => (
                      <div key={s.tier_key}>
                        <button
                          onClick={() => setSeoTier(s.tier_key === seoTier ? null : s.tier_key)}
                          className="w-full p-4 rounded-lg border-2 text-left transition-all"
                          style={seoTier === s.tier_key ? getActiveClasses() : { border: '2px solid #E5E7EB' }}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="font-bold text-lg">{s.name}</div>
                              {s.show_description !== false && s.description && (
                                <div className="text-sm text-gray-600 mt-1">{s.description}</div>
                              )}
                            </div>
                            <div className="text-xl font-bold ml-4" style={getAccentTextColor()}>{fmt(s.price)}/mo</div>
                          </div>
                        </button>
                        {seoTier === s.tier_key && (s.features?.sections || s.features?.length > 0 || s.key_deliverables?.length > 0) && (
                          <div className="mt-2">
                            <button
                              onClick={() => setExpandedService(expandedService === s.tier_key ? null : s.tier_key)}
                              className="w-full px-4 py-2 rounded-lg flex justify-between items-center text-sm font-medium hover:opacity-80 transition-colors"
                              style={getAccentBgClasses()}
                            >
                              <span>View Details</span>
                              {expandedService === s.tier_key ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </button>
                            {expandedService === s.tier_key && (
                              <div className="mt-2 p-4 bg-gray-50 rounded-lg border">
                                {s.features && s.features.sections ? (
                                  renderStructuredFeatures(s.features)
                                ) : (
                                  <>
                                    {s.description && (
                                      <div className="mb-4">
                                        {renderStructuredDescription(s.description)}
                                      </div>
                                    )}
                                    {s.features && Array.isArray(s.features) && s.features.length > 0 && (
                                      <div className="mb-4">
                                        <h5 className="font-semibold text-sm mb-2">Features Included:</h5>
                                        <ul className="space-y-1">
                                          {s.features.map((feature, idx) => (
                                            <li key={idx} className="flex items-start gap-2 text-sm">
                                              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                              <span>{feature}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                    {s.key_deliverables && s.key_deliverables.length > 0 && (
                                      <div>
                                        <h5 className="font-semibold text-sm mb-2">Key Deliverables:</h5>
                                        <ul className="space-y-1">
                                          {s.key_deliverables.map((deliverable, idx) => (
                                            <li key={idx} className="flex items-start gap-2 text-sm">
                                              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                              <span>{deliverable}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {clientType === 'local' && (() => {
                  const multiLocationAddon = additionalPricing.find(p => p.item_key === 'multi_location');
                  const isExpanded = expandedService === 'multi_location';

                  return (
                    <div className="bg-white p-6 rounded-lg shadow-sm">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold">ADD-ON LOCAL BUSINESS SEO</h3>
                          {multiLocationAddon?.description && (
                            <p className="text-sm text-gray-600 mt-2">{multiLocationAddon.description}</p>
                          )}
                        </div>
                        <div className="text-xl font-bold ml-4" style={getAccentTextColor()}>
                          ${getAdditionalPrice('multi_location')}/mo
                        </div>
                      </div>

                      <p className="text-sm text-gray-500 mb-4">per additional location</p>

                      <div className="flex gap-3 items-center mb-4">
                        <button onClick={() => multiLocation > 0 && setMultiLocation(multiLocation - 1)} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors">-</button>
                        <span className="text-2xl font-bold">{multiLocation}</span>
                        <button onClick={() => setMultiLocation(multiLocation + 1)} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors">+</button>
                      </div>

                      {multiLocationAddon?.features && (
                        <div>
                          <button
                            onClick={() => setExpandedService(isExpanded ? null : 'multi_location')}
                            className="flex items-center gap-2 text-sm font-medium hover:underline"
                            style={getAccentTextColor()}
                          >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            {isExpanded ? 'Hide' : 'Show'} Details
                          </button>

                          {isExpanded && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              {renderStructuredFeatures(multiLocationAddon.features)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {clientType === 'ecommerce' && (
                  <div className="bg-white p-6 rounded-lg shadow-sm">
                    <h3 className="text-xl font-bold mb-4">CRO</h3>
                    <div className="grid grid-cols-1 gap-3">
                      {croServices.map(s => (
                        <div key={s.tier_key}>
                          <button
                            onClick={() => setCroTier(s.tier_key === croTier ? null : s.tier_key)}
                            className="w-full p-4 rounded-lg border-2 text-left transition-all"
                            style={croTier === s.tier_key ? getActiveClasses() : { border: '2px solid #E5E7EB' }}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="font-bold text-lg">{s.name}</div>
                                {s.description && (
                                  <div className="text-sm text-gray-600 mt-1">{s.description}</div>
                                )}
                              </div>
                              <div className="text-xl font-bold ml-4" style={getAccentTextColor()}>{fmt(s.price)}/mo</div>
                            </div>
                          </button>
                          {croTier === s.tier_key && (s.features?.sections || s.features?.length > 0 || s.key_deliverables?.length > 0) && (
                            <div className="mt-2">
                              <button
                                onClick={() => setExpandedService(expandedService === s.tier_key ? null : s.tier_key)}
                                className={`w-full px-4 py-2 rounded-lg flex justify-between items-center text-sm font-medium transition-colors ${
                                isWebDesignMiami()
                                  ? 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                              }`}
                              >
                                <span>View Details</span>
                                {expandedService === s.tier_key ? (
                                  <ChevronUp className="w-4 h-4" />
                                ) : (
                                  <ChevronDown className="w-4 h-4" />
                                )}
                              </button>
                              {expandedService === s.tier_key && (
                                <div className="mt-2 p-4 bg-gray-50 rounded-lg border">
                                  {s.features && s.features.sections ? (
                                    renderStructuredFeatures(s.features)
                                  ) : (
                                    <>
                                      {s.description && (
                                        <div className="mb-4">
                                          {renderStructuredDescription(s.description)}
                                        </div>
                                      )}
                                      {s.features && Array.isArray(s.features) && s.features.length > 0 && (
                                        <div className="mb-4">
                                          <h5 className="font-semibold text-sm mb-2">Features Included:</h5>
                                          <ul className="space-y-1">
                                            {s.features.map((feature, idx) => (
                                              <li key={idx} className="flex items-start gap-2 text-sm">
                                                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                                <span>{feature}</span>
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                      {s.key_deliverables && s.key_deliverables.length > 0 && (
                                        <div>
                                          <h5 className="font-semibold text-sm mb-2">Key Deliverables:</h5>
                                          <ul className="space-y-1">
                                            {s.key_deliverables.map((deliverable, idx) => (
                                              <li key={idx} className="flex items-start gap-2 text-sm">
                                                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                                <span>{deliverable}</span>
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <h3 className="text-xl font-bold mb-4">Marketing Discount</h3>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={discountInput}
                      onChange={(e) => setDiscountInput(e.target.value.toUpperCase())}
                      placeholder="Code"
                      className="flex-1 border-2 px-3 py-2 rounded-lg uppercase"
                      disabled={discount !== null}
                    />
                    <button
                      onClick={handleApplyMarketingDiscount}
                      className="px-4 py-2 rounded-lg text-white"
                      style={discount ? { backgroundColor: '#EF4444' } : getButtonClasses()}
                    >
                      {discount ? 'Remove' : 'Apply'}
                    </button>
                  </div>
                  {discount && (
                    <div className="mt-2 p-2 bg-green-50 rounded-lg text-green-800 text-sm flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      Applied
                    </div>
                  )}
                </div>

                {!includeWeb && (
                  <div className="bg-white p-6 rounded-lg shadow-sm">
                    <h3 className="text-xl font-bold mb-4">Website Care & Development Plans</h3>
                    <div className="space-y-2">
                      {supportPlans.map(plan => (
                        <div key={plan.tier_key}>
                          <button
                            onClick={() => setSelectedSupportPlan(selectedSupportPlan === plan.tier_key ? null : plan.tier_key)}
                            className="w-full p-4 rounded-lg border-2 text-left transition-all"
                            style={selectedSupportPlan === plan.tier_key ? getActiveClasses() : { border: '2px solid #E5E7EB' }}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="font-bold text-lg">{plan.name}</div>
                              <div className="font-bold text-xl" style={getAccentTextColor()}>{fmt(plan.price)}/mo</div>
                            </div>
                            <div className="text-sm text-gray-600">{plan.hours} hours included per month</div>
                            {plan.description && (
                              <div className="text-sm text-gray-700 mt-2">{plan.description}</div>
                            )}
                          </button>

                          {selectedSupportPlan === plan.tier_key && plan.features && (
                            (plan.features.sections && Array.isArray(plan.features.sections) && plan.features.sections.length > 0) ||
                            (Array.isArray(plan.features) && plan.features.length > 0)
                          ) && (
                            <div className="mt-2 ml-4">
                              <button
                                onClick={() => setExpandedService(expandedService === plan.tier_key ? null : plan.tier_key)}
                                className={`w-full px-4 py-2 rounded-lg flex justify-between items-center text-sm font-medium transition-colors ${
                                isWebDesignMiami()
                                  ? 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                              }`}
                              >
                                <span>View Details</span>
                                {expandedService === plan.tier_key ? (
                                  <ChevronUp className="w-4 h-4" />
                                ) : (
                                  <ChevronDown className="w-4 h-4" />
                                )}
                              </button>

                              {expandedService === plan.tier_key && (
                                <div className="mt-2 p-4 bg-gray-50 rounded-lg border space-y-4">
                                  {(plan.features.sections || plan.features).map((feature, idx) => (
                                    <div key={idx}>
                                      <h5 className="font-semibold text-sm mb-1">{feature.title}</h5>
                                      {feature.subtitle && (
                                        <p className="text-sm text-gray-600 mb-2">{feature.subtitle}</p>
                                      )}
                                      {feature.items && feature.items.length > 0 && (
                                        <ul className="space-y-1">
                                          {feature.items.map((item, itemIdx) => (
                                            <li key={itemIdx} className="text-sm text-gray-700 flex items-start gap-2">
                                              <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                              <span>{item}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>
                                  ))}

                                  {plan.turnaround_times && (
                                    <div>
                                      <h5 className="font-semibold text-sm mb-2">Turnaround Times</h5>
                                      <div className="text-sm text-gray-700 space-y-1">
                                        {plan.turnaround_times.simple && (
                                          <div className="flex items-start gap-2">
                                            <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                            <span><strong>Simple tasks:</strong> {plan.turnaround_times.simple}</span>
                                          </div>
                                        )}
                                        {plan.turnaround_times.moderate && (
                                          <div className="flex items-start gap-2">
                                            <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                            <span><strong>Moderate tasks:</strong> {plan.turnaround_times.moderate}</span>
                                          </div>
                                        )}
                                        {plan.turnaround_times.complex && (
                                          <div className="flex items-start gap-2">
                                            <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                                            <span><strong>Complex tasks:</strong> {plan.turnaround_times.complex}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <SummaryPanel
            webTotal={webInfo.total}
            webOriginalTotal={webInfo.originalTotal}
            webDiscount={webInfo.discount}
            appliedWebDiscount={appliedWebDiscount}
            marketingMonthly={marketingFees.monthly}
            marketingSetup={marketingFees.setup}
            marketingDiscount={marketingFees.disc}
            marketingBreakdown={marketingFees.breakdown}
            adSpend={adSpend}
            includeWeb={includeWeb}
            includeMarketing={includeMarketing}
            selectedAdPlatforms={selectedAdPlatforms}
            adPlatforms={adPlatforms}
            clientType={clientType}
            discount={discount}
            selectedProduct={pkg}
            seoTier={seoTier}
            emailTier={emailTier}
            croTier={croTier}
            selectedSupportPlan={selectedSupportPlan}
            multiLocation={multiLocation}
            customMarketingHours={customMarketingHours}
            onExportPDF={exportPDF}
            onSaveEstimate={handleSaveEstimate}
            onGenerateShareLink={generateShareLink}
          />
        </div>
      </div>

      {showAdmin && <AdminPanel onClose={() => setShowAdmin(false)} />}
    </div>
  );
}
